// maintainCollection() — the snapshot-semantics wrapper (THE crux of S4).
//
// Firestore `onSnapshot` hands the caller the FULL current collection on every
// change. Supabase Realtime instead emits row-level deltas (INSERT / UPDATE /
// DELETE). This helper bridges the gap so every `subscribe*` honors the
// contract's invariant §1: each `cb(rows)` is the COMPLETE, current array for
// the room, never a delta.
//
// Strategy:
//   1. Subscribe to `postgres_changes` FIRST so a row inserted in the window
//      between subscribe and backfill isn't lost (the Map upsert makes a
//      duplicate delta harmless).
//   2. Run the initial `select` backfill into the same Map.
//   3. On every change, re-emit the FULL array: postFilter → sort → cb.
//   4. Idempotent unsubscribe via removeChannel, guarded by a `closed` flag.
import { getClient } from './client.js';

// Monotonic suffix so every maintainCollection() call gets a UNIQUE channel
// topic. supabase-js keys channels by topic: if two live subscribers use the
// same name (e.g. presence has several at once — DesktopHeader, MembersPanel,
// MeshProvider), the second `.channel()` returns the first's already-subscribed
// instance and `.on(...)` after subscribe() throws "cannot add postgres_changes
// callbacks ... after subscribe()". A per-call suffix keeps them independent.
let channelSeq = 0;

// Primary-key column per table. messages/signals carry a single `id` column;
// presence/typing have composite PKs `(code, user_id)` and no `id`, so we key
// their in-memory cache by `user_id` (unique within a room — and every
// maintainCollection here is already filtered to one `code`). DELETE payloads
// from Postgres include the PK columns by default, so `old[primaryKey]`
// resolves on deletes without REPLICA IDENTITY FULL.
export function primaryKey(table) {
  switch (table) {
    case 'messages':
      return 'id';
    case 'presence':
      return 'user_id';
    case 'typing':
      return 'user_id';
    case 'signals':
      return 'id';
    default:
      return 'id';
  }
}

/**
 * @param {Object}   opts
 * @param {string}   opts.table       e.g. 'messages'
 * @param {{column: string, value: string}} opts.filter   PostgREST filter
 * @param {(row: Object) => Object}   opts.mapRow          row → normalized entry
 * @param {(a, b) => number}          [opts.compare]       applied before each emit
 * @param {(entry) => boolean}        [opts.postFilter]    applied before each emit
 * @param {(rows: Object[]) => void}  opts.cb              full-array callback
 * @returns {() => void}   idempotent unsubscribe
 */
export function maintainCollection({ table, filter, mapRow, compare, postFilter, cb }) {
  const supabase = getClient();
  const pk = primaryKey(table);
  const cache = new Map(); // pk value -> normalized entry
  let closed = false;

  const emit = () => {
    let rows = [...cache.values()];
    if (postFilter) rows = rows.filter(postFilter);
    if (compare) rows.sort(compare);
    cb(rows);
  };

  // 1. realtime channel first (so we don't miss deltas between fetch and sub).
  channelSeq += 1;
  const channel = supabase
    .channel(`${table}:${filter.column}=${filter.value}:${channelSeq}`)
    .on(
      'postgres_changes',
      {
        event: '*',
        schema: 'public',
        table,
        filter: `${filter.column}=eq.${filter.value}`,
      },
      (payload) => {
        if (closed) return;
        const { eventType, new: nu, old } = payload;
        if (eventType === 'DELETE') {
          // DELETE payloads carry only the PK columns in `old` by default.
          cache.delete(old?.[pk]);
        } else {
          cache.set(nu[pk], mapRow(nu));
        }
        emit();
      },
    )
    .subscribe();

  // 2. initial backfill.
  supabase
    .from(table)
    .select('*')
    .eq(filter.column, filter.value)
    .then(({ data, error }) => {
      if (closed || error || !data) return;
      data.forEach((row) => cache.set(row[pk], mapRow(row)));
      emit();
    });

  // 3. idempotent unsubscribe — removeChannel guarded by `closed`.
  return () => {
    if (closed) return;
    closed = true;
    supabase.removeChannel(channel);
  };
}
